import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/ownVehicleReport";

const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

const fmtDate = (v) => {
  if (!v) return "";
  return toStr(v);
};

const fmtNum = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return toStr(v);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

export default function OwnVehicleReportPrint() {
  const { state } = useLocation();
  const navigate = useNavigate();

  // ✅ params passed from OwnVehicleReport.jsx
  const paramsFromState = useMemo(() => {
    const s = state || {};
    return {
      branchType: s.branchType ?? localStorage.getItem("branchtype") ?? "HO",
      loginUserId: s.loginUserId ?? localStorage.getItem("userId") ?? localStorage.getItem("loginuserid") ?? "",
      branchId: s.branchId ?? (localStorage.getItem("branchid") || "Select"),
      status: s.status ?? "Select",
      vehicleTypeIndex: s.vehicleTypeIndex ?? "Select",
      vehicleSubTypeId: s.vehicleSubTypeId ?? "Select",
      storeId: s.storeId ?? "Select",
      vehicleCode: s.vehicleCode ?? "",
      make: s.make ?? "",
      model: s.model ?? "",
      pageSize: Number(s.pageSize ?? 50), // ✅ default 50
      sessionBranchId: s.sessionBranchId ?? Number(localStorage.getItem("branchid") || 0),
    };
  }, [state]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // ✅ Fetch ALL pages using pageSize passed from listing page
  const fetchAllPages = async () => {
    try {
      setLoading(true);
      setErr("");
      setRows([]);

      const pageSize = Math.max(1, paramsFromState.pageSize || 50);

      let page = 1;
      let all = [];
      const MAX_PAGES = 500; // safety cap

      while (page <= MAX_PAGES) {
        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            branchType: paramsFromState.branchType,
            loginUserId: paramsFromState.loginUserId,
            branchId: paramsFromState.branchId,
            status: paramsFromState.status,
            vehicleTypeIndex: paramsFromState.vehicleTypeIndex,
            vehicleSubTypeId: paramsFromState.vehicleSubTypeId,
            storeId: paramsFromState.storeId,
            vehicleCode: paramsFromState.vehicleCode,
            make: paramsFromState.make,
            model: paramsFromState.model,
            page,
            pageSize,
            sessionBranchId: paramsFromState.sessionBranchId,
          },
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load print report");
        }

        const batch = data?.rows || [];
        all = all.concat(batch);

        // ✅ last page condition
        if (batch.length < pageSize) break;

        page += 1;
      }

      setRows(all);
    } catch (e) {
      setErr(e.message || "Failed to load print report");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // If user opened print page directly without state, still try with defaults
    fetchAllPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtersLabel = useMemo(() => {
    const p = paramsFromState;
    return [
      !isSelect(p.branchId) ? `Branch: ${p.branchId}` : null,
      !isSelect(p.status) ? `Status: ${p.status}` : null,
      !isSelect(p.vehicleTypeIndex) ? `Type: ${p.vehicleTypeIndex}` : null,
      !isSelect(p.vehicleSubTypeId) ? `SubType: ${p.vehicleSubTypeId}` : null,
      !isSelect(p.storeId) ? `Store: ${p.storeId}` : null,
      p.vehicleCode ? `Code: ${p.vehicleCode}` : null,
      p.make ? `Make: ${p.make}` : null,
      p.model ? `Model: ${p.model}` : null,
    ]
      .filter(Boolean)
      .join(" | ");
  }, [paramsFromState]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { margin: 0; padding: 10px; font-size: 10pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 8pt; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
        }
        @page {
          size: A4 landscape;
          margin: 0.5in;
        }
      `}</style>

      <div className="p-2 print:p-1">
        {/* Header */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu.
              </div>
              <div className="text-xs">
                Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;infracnsoft-implement
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <h2 className="text-base font-bold">OWN VEHICLE REPORT</h2>
          {filtersLabel ? <div className="text-xs mt-1">{filtersLabel}</div> : null}
          <div className="text-xs mt-1">
            Total Records: <b>{rows.length}</b>
          </div>
        </div>

        {/* Controls */}
        <div className="no-print mb-3 flex gap-2">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 border border-gray-400 rounded"
          >
            Back
          </button>

          <button
            onClick={fetchAllPages}
            className="px-4 py-2 border border-gray-400 rounded"
            disabled={loading}
          >
            {loading ? "Loading..." : "Reload"}
          </button>

          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow-lg"
            disabled={loading || rows.length === 0}
          >
            🖨️ Print Report
          </button>
        </div>

        {err ? (
          <div className="no-print p-3 border border-red-400 text-red-700 rounded mb-3">
            {err}
          </div>
        ) : null}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 font-bold text-center">S.No</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Type</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Code</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Name</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Regno</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Make</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Model</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Chassis Number</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Invoice Number</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Purchase Date</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Purchase Value</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Cum HMR</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Cum KMR</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Branch</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, idx) => (
                <tr key={r?.fld_vehicleid ?? idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="border border-black px-1 py-1 text-center">{idx + 1}</td>
                  <td className="border border-black px-1 py-1">{r?.fld_vehicletypename ?? ""}</td>
                  <td className="border border-black px-1 py-1 text-center">{r?.fld_vehicle_code ?? ""}</td>
                  <td className="border border-black px-1 py-1">{r?.fld_vehicle_name ?? ""}</td>
                  <td className="border border-black px-1 py-1">{r?.fld_vehicle_regno ?? ""}</td>
                  <td className="border border-black px-1 py-1">{r?.fld_vehiclemake ?? ""}</td>
                  <td className="border border-black px-1 py-1">{r?.fld_vehicle_model ?? ""}</td>
                  <td className="border border-black px-1 py-1">{r?.fld_Chassisno ?? ""}</td>
                  <td className="border border-black px-1 py-1 text-center">{r?.fld_invoiceno ?? ""}</td>
                  <td className="border border-black px-1 py-1 text-center">{fmtDate(r?.fld_purchaseddate)}</td>
                  <td className="border border-black px-1 py-1 text-right">{fmtNum(r?.fld_purchased_amt)}</td>
                  <td className="border border-black px-1 py-1 text-right">{fmtNum(r?.fld_cumHMR)}</td>
                  <td className="border border-black px-1 py-1 text-right">{fmtNum(r?.fld_cumKMR)}</td>
                  <td className="border border-black px-1 py-1 text-center">{r?.fld_branchname ?? ""}</td>
                </tr>
              ))}

              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={14} className="border border-black px-2 py-4 text-center">
                    No Records
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
        </div>
      </div>
    </div>
  );
}
